import mitt, { Emitter } from 'mitt'
import { defineAsyncComponent, createApp, nextTick } from 'vue'
import * as Sentry from '@sentry/vue'
import { createRouter, createWebHistory } from 'vue-router'

import { RadioGroup, RadioGroupOption } from '@headlessui/vue'
import { preloadAsyncComponents } from './preload-async-components'

import './bootstrap'
import { highlightCodeBlocksWhenReady } from './highlight-code'
import { enableInlineImageZoom } from './inline-image-zoom'
import { portalMenuOptions } from './portal-menus'
import { repairMailContrastWhenReady } from './repair-mail-contrast'
import { captureInstallPrompt } from './install-prompt'
import { ensurePushDeviceReconciled } from './push-device'
import { registerServiceWorker } from './register-service-worker'
import { routes } from './router-routes'
import { trackUmami } from './umami.js'

highlightCodeBlocksWhenReady()
repairMailContrastWhenReady()
enableInlineImageZoom()

// Before load: the browser fires `beforeinstallprompt` once, and early.
import type { NotificationData } from '@/notification-text'
import type { RuleChange } from '@/vue-components/ticket/rule-changes'
captureInstallPrompt()

// After load: being installable is never worth competing with the page itself.
window.addEventListener('load', () => {
  void registerServiceWorker()

  /**
   * Reconciles this browser's push subscription with the server, here rather
   * than in the bell: the bell is not on every page, and the user portal has no
   * bell at all. A browser where an agent signed out and someone else signed in
   * would otherwise keep the agent's endpoint alive, and their tickets would go
   * on arriving on a phone that is no longer theirs.
   *
   * Costs nothing for a browser that never subscribed — it asks the server only
   * when there is a live subscription to ask about.
   */
  if (window.userid) {
    void ensurePushDeviceReconciled()
  }
})

type FlashLevel = 'success' | 'error' | 'danger'
type AppEvents = {
  flash: { message: string; level: FlashLevel }
  'notification-toast': NotificationData
  /** A ticket's owner changed; every control showing it follows. */
  ticketUpdated: { ticketId: number; ownerId: number | string }
  /** Rules filled in fields after an agent's edit on this ticket. */
  ruleChanges: { ticketId: number; changes: RuleChange[] }
}
declare global {
  interface Window {
    events: Emitter<AppEvents>
    flash: (message: string, level?: FlashLevel) => void
    trackUmami: (event: string, data?: Record<string, unknown>) => void
    sentrydata: {
      user: string | null
      release: string
      environment: string
    }
    csrftoken: string
    language: string
    /** The signed-in user's id, or null for a guest. Set by the layouts. */
    userid: number | null
    __hitJsErrors?: Array<{ time: string; message: string }>
  }
}

const events: Emitter<AppEvents> = mitt<AppEvents>()
window.events = events

window.flash = function (message: string, level: FlashLevel = 'success') {
  window.events.emit('flash', { message, level })
}
window.trackUmami = trackUmami

const router = createRouter({
  history: createWebHistory(),
  routes,
})

const app = createApp(portalMenuOptions)

app.component(
  'UserportalTemplate',
  defineAsyncComponent(
    () => import('./vue-components/userportal/UserportalTemplate.vue'),
  ),
)
app.component(
  'LoadingComponent',
  defineAsyncComponent(
    () => import('./vue-components/settings/LoadingComponent.vue'),
  ),
)
app.component(
  'NewTicketComponent',
  defineAsyncComponent(() => import('./vue-components/NewTicketComponent.vue')),
)
app.component(
  'ExternalCommunicationCreateComponent',
  defineAsyncComponent(
    () => import('./vue-components/ExternalCommunicationCreateComponent.vue'),
  ),
)
app.component(
  'TicketComponent',
  defineAsyncComponent(
    () => import('./vue-components/ticket/TicketComponent.vue'),
  ),
)
app.component(
  'UsersComponent',
  defineAsyncComponent(
    () => import('./vue-components/settings/UsersComponent.vue'),
  ),
)
app.component(
  'SettingsEmailTemplates',
  defineAsyncComponent(
    () => import('./vue-components/settings/SettingsEmailTemplates.vue'),
  ),
)

app.component(
  'SettingsUserportalAccess',
  defineAsyncComponent(
    () => import('./vue-components/settings/SettingsUserportalAccess.vue'),
  ),
)

app.component(
  'SettingsTicketMoves',
  defineAsyncComponent(
    () => import('./vue-components/settings/SettingsTicketMoves.vue'),
  ),
)

app.component(
  'TicketActionsDropdown',
  defineAsyncComponent(
    () => import('./vue-components/ticket/TicketActionsDropdown.vue'),
  ),
)
app.component(
  'SettingsEmailTemplateShow',
  defineAsyncComponent(
    () => import('./vue-components/settings/SettingsEmailTemplateShow.vue'),
  ),
)
app.component(
  'EmailConfigComponent',
  defineAsyncComponent(
    () => import('./vue-components/settings/EmailConfigComponent.vue'),
  ),
)
app.component(
  'TicketCreationComponent',
  defineAsyncComponent(
    () => import('./vue-components/settings/TicketCreationComponent.vue'),
  ),
)

app.component(
  'NewTicketFromTemplate',
  defineAsyncComponent(
    () => import('./vue-components/ticket/NewTicketFromTemplate.vue'),
  ),
)

app.component(
  'ManageCategories',
  defineAsyncComponent(
    () => import('./vue-components/settings/ManageCategories.vue'),
  ),
)
app.component(
  'ManageCustomFields',
  defineAsyncComponent(
    () => import('./vue-components/settings/ManageCustomFields.vue'),
  ),
)

app.component(
  'ManageCustomers',
  defineAsyncComponent(
    () => import('./vue-components/settings/ManageCustomers.vue'),
  ),
)

app.component(
  'CleanupComponent',
  defineAsyncComponent(() => import('./vue-components/CleanupComponent.vue')),
)
app.component(
  'FlashMessage',
  defineAsyncComponent(
    () => import('./vue-components/components/HitFlash.vue'),
  ),
)
app.component(
  'NewEmailComponent',
  defineAsyncComponent(
    () => import('./vue-components/profile/NewEmailComponent.vue'),
  ),
)
app.component(
  'HitTabbable',
  defineAsyncComponent(
    () => import('./vue-components/components/HitTabbable.vue'),
  ),
)
app.component(
  'TabbableData',
  defineAsyncComponent(
    () => import('./vue-components/components/TabbableData.vue'),
  ),
)
app.component(
  'StatusComponent',
  defineAsyncComponent(
    () => import('./vue-components/ticket/StatusComponent.vue'),
  ),
)
app.component(
  'UsernameEditable',
  defineAsyncComponent(
    () => import('./vue-components/profile/UsernameEditable.vue'),
  ),
)
app.component(
  'HitSelect',
  defineAsyncComponent(
    () => import('./vue-components/components/HitSelectBlade.vue'),
  ),
)
app.component(
  'QueueUserSettings',
  defineAsyncComponent(
    () => import('./vue-components/profile/QueueUserSettings.vue'),
  ),
)
app.component(
  'PushDeviceStatus',
  defineAsyncComponent(
    () => import('./vue-components/profile/PushDeviceStatus.vue'),
  ),
)
app.component(
  'ScheduledTicketInstances',
  defineAsyncComponent(
    () => import('./vue-components/ticket/ScheduledTicketInstances.vue'),
  ),
)
app.component(
  'NewTicketFromTemplateComponent',
  defineAsyncComponent(
    () =>
      import('./vue-components/components/NewTicketFromTemplateComponent.vue'),
  ),
)
app.component(
  'RelatedTicket',
  defineAsyncComponent(
    () => import('./vue-components/components/RelatedTicket.vue'),
  ),
)
app.component(
  'TicketAssets',
  defineAsyncComponent(
    () => import('./vue-components/components/TicketAssets.vue'),
  ),
)
app.component(
  'TicketKbArticles',
  defineAsyncComponent(
    () => import('./vue-components/components/TicketKbArticles.vue'),
  ),
)
app.component(
  'TicketParticipants',
  defineAsyncComponent(
    () => import('./vue-components/components/TicketParticipants.vue'),
  ),
)
app.component(
  'TicketHistory',
  defineAsyncComponent(
    () => import('./vue-components/ticket/TicketHistory.vue'),
  ),
)
app.component(
  'HistoryEvents',
  defineAsyncComponent(
    () => import('./vue-components/components/HistoryEvents.vue'),
  ),
)
app.component(
  'edit-scheduled-ticket',
  defineAsyncComponent(
    () => import('./vue-components/EditScheduledticketComponent.vue'),
  ),
)
app.component(
  'HitDatepicker',
  defineAsyncComponent(
    () => import('./vue-components/components/HitDatepicker.vue'),
  ),
)
app.component(
  'HitDuedate',
  defineAsyncComponent(() => import('./vue-components/ticket/HitDuedate.vue')),
)
app.component(
  'CustomFieldValues',
  defineAsyncComponent(
    () => import('./vue-components/ticket/CustomFieldValues.vue'),
  ),
)
app.component(
  'TemplateFieldValues',
  defineAsyncComponent(
    () => import('./vue-components/ticket/TemplateFieldValues.vue'),
  ),
)
app.component(
  'HitHideuntil',
  defineAsyncComponent(
    () => import('./vue-components/ticket/HitHideuntil.vue'),
  ),
)
app.component(
  'HitSelectCategory',
  defineAsyncComponent(
    () => import('./vue-components/ticket/HitSelectCategory.vue'),
  ),
)
app.component(
  'HitSelectCustomer',
  defineAsyncComponent(
    () => import('./vue-components/ticket/HitSelectCustomer.vue'),
  ),
)
app.component(
  'HitSelectOwner',
  defineAsyncComponent(
    () => import('./vue-components/ticket/HitSelectOwner.vue'),
  ),
)
app.component(
  'HitSelectPrio',
  defineAsyncComponent(
    () => import('./vue-components/ticket/HitSelectPrio.vue'),
  ),
)
app.component(
  'HitMoveTicket',
  defineAsyncComponent(
    () => import('./vue-components/ticket/HitMoveTicket.vue'),
  ),
)
app.component(
  'TimereportSelector',
  defineAsyncComponent(() => import('./vue-components/TimereportSelector.vue')),
)
app.component(
  'StatisticsPage',
  defineAsyncComponent(
    () => import('./vue-components/statistics/StatisticsPage.vue'),
  ),
)
app.component(
  'HitNavbar',
  defineAsyncComponent(() => import('./vue-components/HitNavbar.vue')),
)
app.component(
  'HitButton',
  defineAsyncComponent(
    () => import('./vue-components/components/HitButton.vue'),
  ),
)
app.component(
  'HitWebhooks',
  defineAsyncComponent(
    () => import('./vue-components/settings/HitWebhooks.vue'),
  ),
)
app.component(
  'HitWorkflows',
  defineAsyncComponent(
    () => import('./vue-components/settings/HitWorkflows.vue'),
  ),
)
app.component(
  'WorkflowPicker',
  defineAsyncComponent(
    () => import('./vue-components/workflow/WorkflowPicker.vue'),
  ),
)
app.component(
  'WorkflowRunForm',
  defineAsyncComponent(
    () => import('./vue-components/workflow/WorkflowRunForm.vue'),
  ),
)
app.component(
  'WorkflowApprovals',
  defineAsyncComponent(
    () => import('./vue-components/workflow/WorkflowApprovals.vue'),
  ),
)
app.component(
  'WorkflowRuns',
  defineAsyncComponent(
    () => import('./vue-components/workflow/WorkflowRuns.vue'),
  ),
)
app.component(
  'WorkflowApprovalHistory',
  defineAsyncComponent(
    () => import('./vue-components/workflow/WorkflowApprovalHistory.vue'),
  ),
)
app.component(
  'WorkflowRunDetail',
  defineAsyncComponent(
    () => import('./vue-components/workflow/WorkflowRunDetail.vue'),
  ),
)
app.component(
  'WorkflowForm',
  defineAsyncComponent(
    () => import('./vue-components/settings/WorkflowForm.vue'),
  ),
)
app.component(
  'HitPill',
  defineAsyncComponent(() => import('./vue-components/components/HitPill.vue')),
)
app.component(
  'EditCustomer',
  defineAsyncComponent(
    () => import('./vue-components/components/EditCustomer.vue'),
  ),
)
app.component(
  'WebhookShow',
  defineAsyncComponent(
    () => import('./vue-components/settings/WebhookShow.vue'),
  ),
)
app.component(
  'ScheduledTickets',
  defineAsyncComponent(() => import('./vue-components/ScheduledTickets.vue')),
)
app.component(
  'FileInput',
  defineAsyncComponent(
    () => import('./vue-components/components/FileInput.vue'),
  ),
)
app.component(
  'CreateRuleComponent',
  defineAsyncComponent(
    () => import('./vue-components/settings/CreateRuleComponent.vue'),
  ),
)
app.component(
  'EditRuleComponent',
  defineAsyncComponent(
    () => import('./vue-components/settings/EditRuleComponent.vue'),
  ),
)
app.component(
  'PopconfirmDeleteButton',
  defineAsyncComponent(
    () => import('./vue-components/components/PopconfirmDeleteButton.vue'),
  ),
)
app.component(
  'DeleteRuleButton',
  defineAsyncComponent(
    () => import('./vue-components/components/DeleteRuleButton.vue'),
  ),
)
app.component(
  'ReorderRuleButtons',
  defineAsyncComponent(
    () => import('./vue-components/components/ReorderRuleButtons.vue'),
  ),
)
app.component(
  'DeleteScheduledTicketAttachmentButton',
  defineAsyncComponent(
    () =>
      import('./vue-components/components/DeleteScheduledTicketAttachmentButton.vue'),
  ),
)
app.component(
  'HitTinyEditor',
  defineAsyncComponent(() => import('./vue-components/HitTinyEditor.vue')),
)
app.component(
  'AssetTableIndex',
  defineAsyncComponent(
    () => import('./vue-components/settings/asset/AssetTableIndex.vue'),
  ),
)
app.component(
  'AssetTableComponent',
  defineAsyncComponent(
    () => import('./vue-components/settings/asset/AssetTableComponent.vue'),
  ),
)
app.component(
  'AssetComponent',
  defineAsyncComponent(
    () => import('./vue-components/assets/AssetComponent.vue'),
  ),
)
app.component(
  'AssetTable',
  defineAsyncComponent(() => import('./vue-components/assets/AssetTable.vue')),
)
app.component(
  'AssetEditComponent',
  defineAsyncComponent(
    () => import('./vue-components/assets/AssetEditComponent.vue'),
  ),
)
app.component(
  'AssetView',
  defineAsyncComponent(() => import('./vue-components/assets/AssetView.vue')),
)

app.component(
  'AssetFileMapperComponent',
  defineAsyncComponent(
    () => import('./vue-components/assets/AssetFileMapperComponent.vue'),
  ),
)
app.component(
  'AssetTableEdit',
  defineAsyncComponent(
    () => import('./vue-components/settings/asset/AssetTableEdit.vue'),
  ),
)
app.component(
  'MessageTemplates',
  defineAsyncComponent(
    () => import('./vue-components/profile/MessageTemplates.vue'),
  ),
)
app.component(
  'ProfileApi',
  defineAsyncComponent(() => import('./vue-components/profile/ProfileApi.vue')),
)

app.component(
  'ExportStatistics',
  defineAsyncComponent(
    () => import('./vue-components/components/ExportStatistics.vue'),
  ),
)
app.component(
  'SettingsMenu',
  defineAsyncComponent(
    () => import('./vue-components/settings/SettingsMenu.vue'),
  ),
)

app.component(
  'AdvancedSettings',
  defineAsyncComponent(
    () => import('./vue-components/settings/AdvancedSettings.vue'),
  ),
)

app.component(
  'SettingsMenuEmailComponent',
  defineAsyncComponent(
    () => import('./vue-components/settings/MenuEmailComponent.vue'),
  ),
)
app.component(
  'TicketHeader',
  defineAsyncComponent(
    () => import('./vue-components/ticket/TicketHeader.vue'),
  ),
)
app.component(
  'ProfileSettingsMenu',
  defineAsyncComponent(
    () => import('./vue-components/profile/SettingsMenu.vue'),
  ),
)
app.component(
  'SearchComponent',
  defineAsyncComponent(
    () => import('./vue-components/components/SearchComponent.vue'),
  ),
)
app.component(
  'SavedFiltersComponent',
  defineAsyncComponent(
    () => import('./vue-components/components/SavedFiltersComponent.vue'),
  ),
)
app.component(
  'KnowledgeBaseComponent',
  defineAsyncComponent(
    () => import('./vue-components/components/KnowledgeBaseComponent.vue'),
  ),
)
app.component(
  'KnowledgeBaseCreate',
  defineAsyncComponent(
    () => import('./vue-components/components/KnowledgeBaseCreate.vue'),
  ),
)
app.component(
  'KnowledgeBaseCategory',
  defineAsyncComponent(
    () => import('./vue-components/components/KnowledgeBaseCategory.vue'),
  ),
)
app.component(
  'EditKBArticleComponent',
  defineAsyncComponent(
    () => import('./vue-components/components/EditKBArticleComponent.vue'),
  ),
)
app.component(
  'KBArticleComponent',
  defineAsyncComponent(
    () => import('./vue-components/components/KBArticleComponent.vue'),
  ),
)
app.component(
  'KBAttachmentList',
  defineAsyncComponent(
    () => import('./vue-components/components/KBAttachmentList.vue'),
  ),
)
app.component(
  'DeleteKbAttachmentButton',
  defineAsyncComponent(
    () => import('./vue-components/components/DeleteKbAttachmentButton.vue'),
  ),
)
app.component(
  'AttachmentImageViewer',
  defineAsyncComponent(
    () => import('./vue-components/ticket/AttachmentImageViewer.vue'),
  ),
)
app.component(
  'DownloadTicketAttachments',
  defineAsyncComponent(
    () => import('./vue-components/components/DownloadTicketAttachments.vue'),
  ),
)
app.component(
  'ScheduledTicketEnding',
  defineAsyncComponent(
    () => import('./vue-components/scheduledtickets/ScheduledTicketEnding.vue'),
  ),
)

app.component(
  'ChangeQueueName',
  defineAsyncComponent(
    () => import('./vue-components/settings/ChangeQueueName.vue'),
  ),
)

app.component(
  'ChangeQueueSlug',
  defineAsyncComponent(
    () => import('./vue-components/settings/ChangeQueueSlug.vue'),
  ),
)

app.component(
  'QueuePropertiesIndex',
  defineAsyncComponent(
    () => import('./vue-components/settings/QueuePropertiesIndex.vue'),
  ),
)

app.component(
  'ThemeEditor',
  defineAsyncComponent(
    () => import('./vue-components/settings/ThemeEditor.vue'),
  ),
)

app.component(
  'CreateRole',
  defineAsyncComponent(
    () => import('./vue-components/settings/CreateRole.vue'),
  ),
)

app.component(
  'ModifyRole',
  defineAsyncComponent(
    () => import('./vue-components/settings/ModifyRole.vue'),
  ),
)

app.component(
  'StatusIndex',
  defineAsyncComponent(
    () => import('./vue-components/settings/status/StatusIndex.vue'),
  ),
)

app.component(
  'ProfileIndex',
  defineAsyncComponent(
    () => import('./vue-components/profile/ProfileIndex.vue'),
  ),
)

app.component(
  'TwoFaIndex',
  defineAsyncComponent(
    () => import('./vue-components/profile/2fa/TwoFaIndex.vue'),
  ),
)

app.component(
  'EditTicketComponent',
  defineAsyncComponent(
    () => import('./vue-components/ticket/EditTicketComponent.vue'),
  ),
)

app.component(
  'SplitTicketComponent',
  defineAsyncComponent(
    () => import('./vue-components/ticket/SplitTicketComponent.vue'),
  ),
)

app.component(
  'TicketMessageComponent',
  defineAsyncComponent(
    () => import('./vue-components/ticket/TicketMessageComponent.vue'),
  ),
)

app.component(
  'HitCollapsibleSection',
  defineAsyncComponent(
    () => import('./vue-components/components/HitCollapsibleSection.vue'),
  ),
)

app.component(
  'ToggleTicketMessageComponent',
  defineAsyncComponent(
    () => import('./vue-components/ticket/ToggleTicketMessageComponent.vue'),
  ),
)

app.component(
  'EditPrivateMessage',
  defineAsyncComponent(
    () => import('./vue-components/ticket/EditPrivateMessage.vue'),
  ),
)

app.component(
  'AutocompleteSubmitter',
  defineAsyncComponent(
    () => import('./vue-components/AutocompleteSubmitter.vue'),
  ),
)
app.component(
  'HitInput',
  defineAsyncComponent(
    () => import('./vue-components/components/HitInput.vue'),
  ),
)
app.component(
  'HitToggle',
  defineAsyncComponent(
    () => import('./vue-components/components/HitToggle.vue'),
  ),
)
app.component(
  'RecipientComponent',
  defineAsyncComponent(
    () => import('./vue-components/ticket/RecipientComponent.vue'),
  ),
)
app.component(
  'AssignTicketSelf',
  defineAsyncComponent(
    () => import('./vue-components/ticket/AssignTicketSelf.vue'),
  ),
)

app.component(
  'UserportalCreateFromTemplate',
  defineAsyncComponent(
    () =>
      import('./vue-components/userportal/UserportalCreateFromTemplate.vue'),
  ),
)

app.component(
  'UserportalCreateInQueue',
  defineAsyncComponent(
    () => import('./vue-components/userportal/UserportalCreateInQueue.vue'),
  ),
)

app.component(
  'HitPanel',
  defineAsyncComponent(
    () => import('./vue-components/components/HitPanel.vue'),
  ),
)

app.component(
  'UserportalTicketCommentForm',
  defineAsyncComponent(
    () => import('./vue-components/userportal/UserportalTicketCommentForm.vue'),
  ),
)

app.component(
  'QueueSelector',
  defineAsyncComponent(
    () => import('./vue-components/components/tickets/QueueSelector.vue'),
  ),
)

app.component(
  'TicketlistPage',
  defineAsyncComponent(
    () => import('./vue-components/pages/TicketlistPage.vue'),
  ),
)

app.component(
  'OverviewPage',
  defineAsyncComponent(() => import('./vue-components/pages/OverviewPage.vue')),
)

app.component(
  'NotificationsHistoryPage',
  defineAsyncComponent(
    () => import('./vue-components/pages/NotificationsHistoryPage.vue'),
  ),
)

app.component(
  'AssetTableSchemaImport',
  defineAsyncComponent(
    () => import('./vue-components/settings/asset/AssetTableSchemaImport.vue'),
  ),
)

app.component(
  'NotificationToast',
  defineAsyncComponent(
    () => import('./vue-components/components/NotificationToast.vue'),
  ),
)

app.component(
  'FeedbackWidget',
  defineAsyncComponent(
    () => import('./vue-components/components/FeedbackWidget.vue'),
  ),
)

app.component(
  'InstallAppBanner',
  defineAsyncComponent(
    () => import('./vue-components/components/InstallAppBanner.vue'),
  ),
)

app.component(
  'DocsWidget',
  defineAsyncComponent(
    () => import('./vue-components/components/DocsWidget.vue'),
  ),
)

app.component(
  'QueueSetupWizard',
  defineAsyncComponent(
    () => import('./vue-components/setup/QueueSetupWizard.vue'),
  ),
)

// Registered globally so Blade views can use <radio-group>/<radio-group-option>
// directly, the same way HitToggle exposes headlessui's native-form Switch.
app.component('RadioGroup', RadioGroup)
app.component('RadioGroupOption', RadioGroupOption)

const configs = [
  {
    domain: 'support.devpeak.se',
    sampleRate: 1.0,
  },
  {
    domain: 'arende.ssis.nu',
    sampleRate: 1.0,
  },
  {
    domain: 'egensupport.se',
    sampleRate: 1.0,
  },
  {
    domain: 'ostrareal.devpeak.se',
    sampleRate: 1.0,
  },
  {
    domain: 'demo.dpit.se',
    sampleRate: 1.0,
  },
  {
    domain: 'localhost',
    sampleRate: 1.0,
  },
  {
    domain: /^\//, // Needed for vue to automatically inject sentry-trace header on outgoing requests
    sampleRate: 1.0,
  },
]

app.config.errorHandler = (msg, vm, info) => {
  console.error(msg, vm, info)
}

Sentry.init({
  app,
  dsn: 'https://f73d633affa5487886eb06a721f09a10@sentry.rm-rf.se/3',
  tunnel: '/sentry-tunnel',
  integrations: [Sentry.browserTracingIntegration({ router })],
  logErrors: false,
  trackComponents: true,
  environment: window.sentrydata.environment,
  release: window.sentrydata.release,
  sampleRate: 1.0,
  tracesSampleRate:
    configs.filter((row) => {
      return row.domain === window.location.hostname
    })?.[0]?.sampleRate ?? '1.0',
  tracePropagationTargets: configs.map((row) => {
    return row.domain
  }),
  beforeSend(event, hint) {
    const buffer = (window.__hitJsErrors ||= [])
    const message =
      (hint?.originalException as Error | undefined)?.message ||
      event.exception?.values?.[0]?.value ||
      event.message
    if (message) {
      buffer.push({
        time: new Date().toISOString(),
        message: String(message).slice(0, 1000),
      })
      window.__hitJsErrors = buffer.slice(-10)
    }
    return event
  },
})

if (window.sentrydata.user) {
  Sentry.setUser({ email: window.sentrydata.user })
}

app.use(router)

const appRoot = document.getElementById('app')!

// Read from the template before mounting replaces it. The app still mounts
// right away, before DOMContentLoaded, which the page's jQuery handlers and the
// mail contrast pass depend on; only showing it waits for the components.
const componentsLoaded = preloadAsyncComponents(app, appRoot)

app.mount(appRoot)

componentsLoaded
  .then(() => nextTick())
  .then(() => {
    appRoot.style.display = ''
    performance.mark('hit:app-visible')
  })
